/**
 * Fully controlled section editor. Every control patches the page's `sections`
 * array live, per keystroke — there is no local buffer, so neither button
 * discards edited field values. `Close` (button, Escape, or the overlay) only
 * dismisses — it never fires a PATCH. `Save` asks the page to persist the
 * template; if the resulting payload is invalid (blank section name, or a
 * blank `aiInstructions` while AI is allowed) the page refuses to commit and
 * the drawer stays open so the inline error remains visible, exactly as an
 * invalid Save must per the page's persistence model.
 */
import {
  Checkbox,
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldSeparator,
  FieldTitle,
  Input,
  Textarea,
} from '@monksflow/monks-ui';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FormSheet } from '@/shared/ui/FormSheet';
import type { SectionDraft } from '../../lib/sectionDraft';
import { BriefSectionTypeIcon } from '../BriefSectionTypeIcon';
import { BriefSectionConfigFields } from './BriefSectionConfigFields';
import { useBriefSectionDrawer } from './hooks/useBriefSectionDrawer';

const BLANK_SECTION: SectionDraft = {
  tempId: '',
  serverId: null,
  name: '',
  required: false,
  aiAllowed: false,
  content: undefined,
  position: 0,
  instructions: null,
  type: 'text',
  config: {},
  allowFiles: false,
  aiInstructions: null,
  isNew: false,
  isDeleted: false,
};

export interface BriefSectionDrawerProps {
  /** `null` closes the drawer. */
  section: SectionDraft | null;
  /** The same section as persisted, so the drawer can judge it the way the save gate does. */
  persistedSection?: SectionDraft;
  disabled: boolean;
  onPatch: (tempId: string, patch: Partial<SectionDraft>) => void;
  /** Close/Escape/overlay dismissal. Never persists. */
  onClose: () => void;
  /** The Save button. Asks the page to persist; the page keeps the drawer open (by not
   * calling back) when the resulting payload is invalid. */
  onSave: () => void;
  /** Hard-remove (no confirm) for a new row dismissed with no name — mirrors the inline-row behavior. */
  onRemoveIfNew: (tempId: string) => void;
}

export function BriefSectionDrawer(props: BriefSectionDrawerProps) {
  const { t } = useTranslation('knowledge');
  const activeSection = props.section ?? BLANK_SECTION;

  const drawer = useBriefSectionDrawer(activeSection, props.persistedSection, (patch) => {
    if (props.section) props.onPatch(props.section.tempId, patch);
  });

  // An unnamed new row is abandoned, never persisted — matching the prototype's
  // `btCloseCfg`. It never reached the server, so there is nothing to write; this check
  // runs before either button's own behavior and short-circuits both to a plain close.
  const discardIfUnnamed = (): boolean => {
    if (!props.section) return false;
    if (props.section.isNew && props.section.name.trim() === '') {
      props.onRemoveIfNew(props.section.tempId);
      return true;
    }
    return false;
  };

  // Always closes — matching the prototype's btCfgDone, which has no guard. A blank
  // aiInstructions with AI on is surfaced inline (aiInstructionsInvalid marks the field);
  // Close never blocks on it, it simply never persists it either.
  const handleClose = () => {
    discardIfUnnamed();
    props.onClose();
  };

  const handleSave = () => {
    if (discardIfUnnamed()) {
      props.onClose();
      return;
    }
    props.onSave();
  };

  return (
    <FormSheet
      open={props.section !== null}
      onOpenChange={(next: boolean) => !next && handleClose()}
      data-testid="brief-section-drawer"
      // 640 over the shell's 30rem default: the width this flow's sheets share.
      className="w-[640px]"
      title={
        // The name as it is being typed, falling back to a placeholder until the section
        // has one — so a row opened from the table is titled by what it is. The icon is
        // decorative by default (no `label`): the title is this sheet's accessible name.
        <span className="flex items-center gap-xs">
          <BriefSectionTypeIcon
            type={activeSection.type}
            className="size-4 shrink-0 text-muted-foreground"
          />
          {activeSection.name.trim() ||
            t(
              activeSection.isNew
                ? 'knowledgeHub.briefSectionDrawer.newSection'
                : 'knowledgeHub.briefSectionDrawer.untitled',
            )}
        </span>
      }
    >
      {props.section && (
        <FormSheet.Body className="flex flex-col gap-4">
          <Field>
            <FieldLabel htmlFor="brief-section-drawer-name">
              {t('knowledgeHub.briefSectionDrawer.name')}
              <span aria-hidden className="text-destructive">
                *
              </span>
            </FieldLabel>
            <Input
              id="brief-section-drawer-name"
              required
              value={activeSection.name}
              disabled={props.disabled}
              placeholder={t('knowledgeHub.briefSectionDrawer.enterSectionName')}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                drawer.handleNameChange(event.target.value)
              }
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="brief-section-drawer-description">
              {t('knowledgeHub.briefSectionDrawer.description')}
            </FieldLabel>
            <Textarea
              id="brief-section-drawer-description"
              rows={2}
              value={activeSection.instructions ?? ''}
              disabled={props.disabled}
              placeholder={t('knowledgeHub.briefSectionDrawer.addDescription')}
              onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                drawer.handleInstructionsChange(event.target.value)
              }
            />
            <FieldDescription>
              {t('knowledgeHub.briefSectionDrawer.descriptionHelper')}
            </FieldDescription>
          </Field>

          {/* Keyed per section: the config fields hold in-progress input that belongs to one section. */}
          <BriefSectionConfigFields
            key={activeSection.tempId}
            type={activeSection.type}
            config={activeSection.config}
            onChange={drawer.handleConfigChange}
            disabled={props.disabled}
          />

          <FieldSeparator />

          {/* Each option is a checkbox with its consequence spelled out beside it, in the
                prototype's order — the AI textarea belongs to the AI checkbox, not beside it. */}
          <Field orientation="horizontal">
            <Checkbox
              id="brief-section-drawer-required"
              aria-labelledby="brief-section-drawer-required-title"
              aria-describedby="brief-section-drawer-required-description"
              checked={activeSection.required}
              disabled={props.disabled}
              onCheckedChange={(next: boolean | 'indeterminate') =>
                drawer.handleRequiredChange(next === true)
              }
            />
            <FieldContent>
              <FieldTitle id="brief-section-drawer-required-title">
                {t('knowledgeHub.briefSectionDrawer.required')}
              </FieldTitle>
              <FieldDescription id="brief-section-drawer-required-description">
                {t('knowledgeHub.briefSectionDrawer.requiredDescription')}
              </FieldDescription>
            </FieldContent>
          </Field>

          <Field orientation="horizontal">
            <Checkbox
              id="brief-section-drawer-ai-allowed"
              aria-labelledby="brief-section-drawer-ai-allowed-title"
              aria-describedby="brief-section-drawer-ai-allowed-description"
              checked={activeSection.aiAllowed}
              disabled={props.disabled}
              onCheckedChange={(next: boolean | 'indeterminate') =>
                drawer.handleAiAllowedChange(next === true)
              }
            />
            <FieldContent>
              <FieldTitle id="brief-section-drawer-ai-allowed-title">
                {t('knowledgeHub.briefSectionDrawer.aiAllowed')}
              </FieldTitle>
              <FieldDescription id="brief-section-drawer-ai-allowed-description">
                {t('knowledgeHub.briefSectionDrawer.aiAllowedDescription')}
              </FieldDescription>
              {activeSection.aiAllowed && (
                <Field data-invalid={drawer.aiInstructionsInvalid || undefined}>
                  <Textarea
                    id="brief-section-drawer-ai-instructions"
                    rows={3}
                    aria-label={t('knowledgeHub.briefSectionDrawer.aiInstructions')}
                    aria-invalid={drawer.aiInstructionsInvalid}
                    value={drawer.displayedAiInstructions}
                    disabled={props.disabled}
                    placeholder={t('knowledgeHub.briefSectionDrawer.aiInstructionsPlaceholder')}
                    onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                      drawer.handleAiInstructionsChange(event.target.value)
                    }
                  />
                  {drawer.aiInstructionsInvalid && (
                    <FieldError>
                      {t('knowledgeHub.briefSectionDrawer.aiInstructionsRequired')}
                    </FieldError>
                  )}
                </Field>
              )}
            </FieldContent>
          </Field>

          {activeSection.type !== 'file' && (
            <Field orientation="horizontal">
              <Checkbox
                id="brief-section-drawer-allow-files"
                aria-labelledby="brief-section-drawer-allow-files-title"
                aria-describedby="brief-section-drawer-allow-files-description"
                checked={activeSection.allowFiles}
                disabled={props.disabled}
                onCheckedChange={(next: boolean | 'indeterminate') =>
                  drawer.handleAllowFilesChange(next === true)
                }
              />
              <FieldContent>
                <FieldTitle id="brief-section-drawer-allow-files-title">
                  {t('knowledgeHub.briefSectionDrawer.allowFiles')}
                </FieldTitle>
                <FieldDescription id="brief-section-drawer-allow-files-description">
                  {t('knowledgeHub.briefSectionDrawer.allowFilesDescription')}
                </FieldDescription>
              </FieldContent>
            </Field>
          )}
        </FormSheet.Body>
      )}

      <FormSheet.Footer
        secondary={{
          label: t('knowledgeHub.briefSectionDrawer.close'),
          'data-testid': 'brief-section-drawer-close',
          onClick: handleClose,
        }}
        primary={{
          label: t('knowledgeHub.briefSectionDrawer.save'),
          'data-testid': 'brief-section-drawer-save',
          onClick: handleSave,
          disabled: props.disabled,
        }}
      />
    </FormSheet>
  );
}
